import { useEffect, useRef, useState } from 'react';
import { Loader2, Pencil } from 'lucide-react';
import Dropdown from '../receptura/Dropdown';
import { ACCENT as SECTION_COLOR, OK, BAD } from '../../lib/uiTokens';

/**
 * Údaj hlavičky dokladu, který se edituje klepnutím na hodnotu.
 *
 * Dřív měla celá hlavička režim: „Upravit" překlopilo dvanáct párů
 * popisek/hodnota na dvanáct inputů, člověk opravil jedno datum a pak hledal
 * Uložit. Tady je pole samo o sobě — klepnutím se otevře, Enter nebo odchod
 * fokusu uloží, Esc zahodí. Ukládá se jen to jedno pole, takže se nemá co
 * přepsat vedle.
 *
 * Read mode NENÍ `div` s `onClick`: je to `<button>`, aby se na pole dalo
 * dostat Tabem a klávesnicí (norma přístupnosti).
 *
 * `inline` je varianta do věty (hlavička dokladu): bez popisku, s typografií
 * okolního textu a tečkovaným podtržením místo rámečku — údaj se tak čte jako
 * text, ne jako formulářové pole, a přitom je vidět, že jde přepsat.
 *
 * @param {'text'|'number'|'date'|'dropdown'} typ
 * @param {(hodnota: any) => Promise<boolean>} onSave  vrací, zda se uložilo
 */
export default function InlinePole({
    label, hodnota, zobrazeni, typ = 'text', options = [], placeholder = '—',
    onSave, editovatelne = true, isDark = false, align = 'left', krok = '0.01',
    napoveda = null, styl = null, sirkaInputu = null, inline = false,
}) {
    const [edituje, setEdituje] = useState(false);
    const [draft, setDraft] = useState('');
    const [uklada, setUklada] = useState(false);
    const [uspech, setUspech] = useState(false);
    const [chyba, setChyba] = useState(false);
    const inputRef = useRef(null);

    // Blesk „uloženo" — potvrzení, že klepnutí a odchod fokusu něco udělaly.
    useEffect(() => {
        if (!uspech) return;
        const t = setTimeout(() => setUspech(false), 1400);
        return () => clearTimeout(t);
    }, [uspech]);

    useEffect(() => {
        if (edituje && typ !== 'dropdown') inputRef.current?.focus();
    }, [edituje, typ]);

    const zacni = () => {
        if (!editovatelne) return;
        setChyba(false);
        setDraft(hodnota ?? '');
        setEdituje(true);
    };

    const uloz = async (novaHodnota) => {
        const puvodni = hodnota ?? '';
        const nova = novaHodnota ?? '';
        setEdituje(false);
        if (String(nova) === String(puvodni)) return;   // nic se nezměnilo
        setUklada(true);
        const ok = await onSave?.(nova === '' ? null : nova);
        setUklada(false);
        if (ok) setUspech(true); else setChyba(true);
    };

    const zrus = () => { setEdituje(false); setChyba(false); };

    const plocha = isDark ? 'rgba(255,255,255,0.04)' : 'var(--surface-base)';

    if (inline) {
        if (edituje && typ !== 'dropdown') {
            return (
                <input
                    ref={inputRef}
                    type={typ === 'number' ? 'number' : typ === 'date' ? 'date' : 'text'}
                    step={typ === 'number' ? krok : undefined}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={() => uloz(draft)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
                        if (e.key === 'Escape') { e.preventDefault(); zrus(); }
                    }}
                    className="rounded-md px-1.5"
                    style={{
                        ...styl, width: sirkaInputu || 150, background: plocha,
                        border: `1px solid ${SECTION_COLOR}`, color: 'var(--text-primary)',
                        outline: 'none', minWidth: 0,
                    }}
                />
            );
        }
        if (edituje) {
            return (
                <span style={{ display: 'inline-block', width: sirkaInputu || 150, verticalAlign: 'middle' }}>
                    <Dropdown value={hodnota ?? ''} onChange={(v) => uloz(v)} options={options}
                        placeholder={placeholder} isDark={isDark} accent={SECTION_COLOR} height={28} />
                </span>
            );
        }
        return (
            <button type="button" onClick={zacni} disabled={!editovatelne}
                title={editovatelne ? (napoveda || 'Klepnutím upravíš') : napoveda || undefined}
                style={{
                    ...styl,
                    background: 'none', border: 'none', padding: 0, margin: 0,
                    cursor: editovatelne ? 'text' : 'default',
                    color: (zobrazeni ?? hodnota) ? (styl?.color || 'var(--text-primary)') : 'var(--text-tertiary)',
                    borderBottom: editovatelne ? '1px dashed var(--border-default)' : 'none',
                    opacity: uklada ? 0.55 : 1,
                }}>
                {zobrazeni ?? hodnota ?? placeholder}
            </button>
        );
    }

    return (
        <div>
            {/* Bez popisku (buňka tabulky) se řádek nekreslí vůbec — jinak by
                každá buňka rozpadu DPH nesla prázdný čtrnáctipixelový pruh. */}
            {(label || uklada || uspech || chyba) && (
                <div className="flex items-center gap-1.5">
                    {label && <span className="text-[10.5px]" style={{ color: 'var(--text-tertiary)' }}>{label}</span>}
                    {uklada && <Loader2 size={10} className="animate-spin" style={{ color: 'var(--text-tertiary)' }} />}
                    {uspech && <span className="text-[10px] font-semibold" style={{ color: OK }}>uloženo</span>}
                    {chyba && <span className="text-[10px] font-semibold" style={{ color: BAD }}>neuloženo</span>}
                </div>
            )}

            {edituje ? (
                typ === 'dropdown' ? (
                    <Dropdown value={hodnota ?? ''} onChange={(v) => uloz(v)} options={options}
                        placeholder={placeholder} isDark={isDark} accent={SECTION_COLOR} height={30} />
                ) : (
                    <input
                        ref={inputRef}
                        type={typ === 'number' ? 'number' : typ === 'date' ? 'date' : 'text'}
                        step={typ === 'number' ? krok : undefined}
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onBlur={() => uloz(draft)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
                            if (e.key === 'Escape') { e.preventDefault(); zrus(); }
                        }}
                        className="w-full rounded-md px-2"
                        style={{
                            height: 30, background: plocha, border: `1px solid ${SECTION_COLOR}`,
                            color: 'var(--text-primary)', fontSize: 13, outline: 'none',
                            textAlign: align === 'right' ? 'right' : 'left',
                            fontVariantNumeric: typ === 'number' ? 'tabular-nums' : undefined,
                        }}
                    />
                )
            ) : (
                <button type="button" onClick={zacni} disabled={!editovatelne}
                    title={editovatelne ? (napoveda || 'Klepnutím upravíš') : napoveda || undefined}
                    className="group w-full flex items-center gap-1.5 rounded-md"
                    style={{
                        minHeight: 30, padding: '2px 0', background: 'none', border: 'none',
                        cursor: editovatelne ? 'text' : 'default',
                        textAlign: align === 'right' ? 'right' : 'left',
                    }}>
                    <span className="flex-1 min-w-0 truncate" style={{
                        fontSize: 13, color: (zobrazeni ?? hodnota) ? 'var(--text-primary)' : 'var(--text-tertiary)',
                        fontVariantNumeric: typ === 'number' ? 'tabular-nums' : undefined,
                        textAlign: align === 'right' ? 'right' : 'left',
                        borderBottom: editovatelne ? `1px dashed transparent` : 'none',
                    }}>
                        {zobrazeni ?? hodnota ?? placeholder}
                    </span>
                    {editovatelne && (
                        <Pencil size={11} className="opacity-0 group-hover:opacity-60 flex-shrink-0"
                            style={{ color: 'var(--text-tertiary)' }} />
                    )}
                </button>
            )}
        </div>
    );
}
